const API_BASE = import.meta.env.VITE_API_URL || 'https://mawkaa-api.yassine-techini.workers.dev/api';

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// In-memory token storage (more secure than localStorage for access tokens)
let accessToken: string | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  // Get token from memory
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    // Include cookies for refresh token
    credentials: 'include',
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE}${endpoint}`, config);

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: { code: 'UNKNOWN', message: 'An error occurred' },
    }));

    // Handle token expiration
    if (response.status === 401 && error.error?.code === 'TOKEN_EXPIRED') {
      // Try to refresh token (uses httpOnly cookie automatically)
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        // Retry the request
        return request<T>(endpoint, options);
      }
      // Clear in-memory token and redirect to login
      accessToken = null;
      window.location.href = '/login';
    }

    throw new ApiError(
      error.error?.code || 'UNKNOWN',
      error.error?.message || 'An error occurred',
      response.status,
      error.error?.details
    );
  }

  return response.json();
}

async function refreshAccessToken(): Promise<boolean> {
  try {
    // Refresh token is sent automatically via httpOnly cookie
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Include cookies
      body: JSON.stringify({}),
    });

    if (!response.ok) return false;

    const data = await response.json();
    accessToken = data.accessToken;
    return true;
  } catch {
    return false;
  }
}

// Convenience methods
export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint),
  post: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: 'POST', body }),
  put: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: 'PUT', body }),
  patch: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: 'PATCH', body }),
  delete: <T>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),
};

// Upload helper
export async function uploadFile(file: File, siteId?: string): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append('file', file);
  if (siteId) formData.append('siteId', siteId);

  const headers: Record<string, string> = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE}/media/upload`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new ApiError(
      error.error?.code || 'UPLOAD_FAILED',
      error.error?.message || 'Upload failed',
      response.status
    );
  }

  return response.json();
}

export { ApiError };
